/**
 * RebuildCampaignModal - Confirmation dialog before entering Restructure mode
 * 
 * Shows when user clicks "Restructure" on a campaign.
 * Explains what will happen and requires explicit confirmation.
 */
import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Layers, AlertTriangle } from 'lucide-react';

interface RebuildCampaignModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    campaignName: string;
    isLoading?: boolean;
}

export function RebuildCampaignModal({
    isOpen,
    onClose,
    onConfirm,
    campaignName,
    isLoading = false,
}: RebuildCampaignModalProps) {
    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Layers className="h-5 w-5" />
                        Restructure Campaign
                    </DialogTitle>
                    <DialogDescription>
                        Re-enter the campaign builder to restructure "{campaignName}".
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <p className="text-sm text-muted-foreground">
                        This opens the campaign builder with the current structure pre-loaded.
                        You can change groups, stores, formats, quantities, and operators.
                    </p>

                    <Alert className="bg-amber-50 border-amber-200">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <AlertDescription className="text-amber-800 text-sm">
                            <strong>Changes won't apply until you confirm.</strong> You'll see an
                            Execution Preview before finalizing any changes.
                        </AlertDescription>
                    </Alert>

                    <p className="text-sm text-muted-foreground">
                        This is different from Quick Edit (metadata only) and Detailed Edit (mutations within snapshot).
                        Restructure allows full group, store, and format re-authoring.
                    </p>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button onClick={onConfirm} disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                Loading...
                            </>
                        ) : (
                            <>
                                <Layers className="h-4 w-4 mr-2" />
                                Continue to Restructure
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
